import { Align, Color, TypographyAlign, TypographyColor } from "../components/typography"
import { BackgroundVideo, VideoAspectRatio } from "../components/video"
import { Container, ContainerType, Spacer, SpacerVariant, spacerOptionsAll } from "../components/layout"
import ImageEditor, { AspectRatio } from "../Image"
import React, { useState } from "react"
import { Repeater, types, useAdminContext } from "react-bricks/frontend"
import Typography, { TypographyElement } from "../RedesignTypography"

import { ConditionalRenderer } from "../components/util"
import { Svgs } from "../../types"
import classNames from "classnames"

interface IconTextShowMoreRedesignProps {
    spacerTop?: SpacerVariant
    spacerBottom?: SpacerVariant
    headline: TypographyElement[]
    cloudflareVideoId?: string
    cloudflareVideoIdMobile?: string
    buttonTitle: string
    initiallyVisible: number
    repeater?: {
        index?: number
        headline: TypographyElement[]
        text: TypographyElement[]
        color?: TypographyColor
        icon: Svgs
    }[]
}

const IconTextShowMoreRedesign = ({
    spacerTop,
    spacerBottom,
    cloudflareVideoId,
    cloudflareVideoIdMobile,
    buttonTitle,
    initiallyVisible,
    repeater,
}: IconTextShowMoreRedesignProps) => {
    const { isAdmin } = useAdminContext()

    const [allVisible, setAllVisible] = useState(false)

    return (
        <section className="relative" style={isAdmin ? { "--header-height": "0px" } : undefined}>
            <div className="sticky top-header h-screen-w/o-header w-full overflow-hidden">
                <div className="absolute top-0 h-full w-full sm:hidden">
                    <ImageEditor
                        aspectRatio={AspectRatio["9/19"]}
                        maxWidth={767}
                        className="absolute top-0 h-full w-full object-cover"
                        propName="imageMobile"
                    />
                    <BackgroundVideo id={cloudflareVideoIdMobile} aspectRatio={VideoAspectRatio["9/16"]} />
                </div>
                <div className="absolute top-0 hidden h-full w-full sm:block">
                    <ImageEditor
                        aspectRatio={AspectRatio["16/9"]}
                        maxWidth={1920}
                        className="absolute top-0 h-full w-full object-cover"
                        propName="image"
                    />
                    <BackgroundVideo id={cloudflareVideoId} aspectRatio={VideoAspectRatio["16/9"]} />
                </div>
            </div>
            <div className="relative -mt-screen-w/o-header min-h-screen-w/o-header">
                <Container type={ContainerType.Redesign}>
                    <Spacer variant={spacerTop} />
                    <Color color={TypographyColor.White}>
                        <Align
                            align={TypographyAlign["Center + mobile left"]}
                            className="w-[200px] xs:w-[250px] sm:w-full"
                        >
                            <Typography propName="headline" allowList={["S1"]} />
                        </Align>
                        <ul className="mt-9 grid w-full justify-start gap-16 xs:mt-[52px] xs:gap-20 sm:mt-10 sm:grid-cols-1 sm:gap-0 lg:mt-15 xl:mt-20">
                            <Repeater
                                propName="repeater"
                                itemProps={{
                                    color: TypographyColor.White,
                                }}
                                renderItemWrapper={(item, index) => (
                                    <ConditionalRenderer condition={allVisible || index < initiallyVisible} key={index}>
                                        {item}
                                    </ConditionalRenderer>
                                )}
                            />
                        </ul>
                        <button
                            onClick={() => setAllVisible(true)}
                            className={classNames(
                                "mx-auto mt-16 flex h-[58px] items-center rounded-full border border-solid border-white px-10 text-base font-semibold sm:h-15 sm:text-md lg:h-[72px] lg:text-lg",
                                (allVisible || (repeater?.length ?? 0) <= initiallyVisible) && "hidden"
                            )}
                        >
                            {buttonTitle}
                        </button>
                    </Color>
                    <Spacer variant={spacerBottom} />
                </Container>
            </div>
        </section>
    )
}

IconTextShowMoreRedesign.schema = {
    name: "IconTextShowMoreRedesign",
    label: "Icon Text Show More Redesign",
    previewImageUrl: `${process.env.IMG_CDN_URL_ASSETS}/lavita.de/startseite/screenshots_bricks/IconTextShowMoreRedesign.png`,
    repeaterItems: [
        {
            name: "repeater",
            items: [
                {
                    type: "IconTextShowMoreRedesignItem",
                    label: "Icon Text Show More Redesign Item",
                    min: 1,
                },
            ],
        },
    ],
    sideEditProps: [
        {
            groupName: "Spacer",
            props: [
                {
                    name: "spacerTop",
                    label: "Spacer Top",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: spacerOptionsAll,
                        display: types.OptionsDisplay.Select,
                    },
                },
                {
                    name: "spacerBottom",
                    label: "Spacer Bottom",
                    type: types.SideEditPropType.Select,
                    selectOptions: {
                        options: spacerOptionsAll,
                        display: types.OptionsDisplay.Select,
                    },
                },
            ],
        },
        {
            name: "buttonTitle",
            label: "Button Title",
            type: types.SideEditPropType.Text,
        },
        {
            name: "initiallyVisible",
            label: "Initially Visible",
            type: types.SideEditPropType.Number,
        },
        {
            name: "cloudflareVideoId",
            label: "Cloudflare Video ID 16x9",
            type: types.SideEditPropType.Text,
        },
        {
            name: "image",
            label: "Image Desktop",
            type: types.SideEditPropType.Image,
        },
        {
            name: "cloudflareVideoIdMobile",
            label: "Cloudflare Video ID (Mobile) 9x16",
            type: types.SideEditPropType.Text,
        },
        {
            name: "imageMobile",
            label: "Image Mobile",
            type: types.SideEditPropType.Image,
        },
    ],
    getDefaultProps: (): IconTextShowMoreRedesignProps => ({
        spacerTop: SpacerVariant["3XL"],
        spacerBottom: SpacerVariant["3XL"],
        headline: [
            {
                type: "h2",
                children: [
                    {
                        text: "Lorem ipsum dolor sit amet",
                        S1: true,
                    },
                ],
            },
        ],
        buttonTitle: "Lorem ipsum",
        initiallyVisible: 8,
    }),
}

export default IconTextShowMoreRedesign
